import React, { useState, useEffect } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  TablePagination,
  Paper,
  IconButton,
  Box,
  Grid,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  CircularProgress,
  Autocomplete,
  Card,
  CardHeader,
  CardContent,
  Container,
  Divider,
  MenuItem,
  Tabs,
  Tab,
  Checkbox,
  FormControlLabel,
  Switch
} from "@mui/material";
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { useParams } from "react-router-dom";
import { apiClient } from "../../../../globals/utils/api";
import Navbar from "../../../../globals/components/Navbar";
import { toast } from 'react-toastify';
import InputMask from 'react-input-mask';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title as ChartTitle,
  Tooltip as ChartTooltip,
  Legend
} from 'chart.js';
import { Bar } from 'react-chartjs-2';
import TablePaginationActions from "../../../../globals/components/TablePaginationActions";

ChartJS.register(CategoryScale, LinearScale, BarElement, ChartTitle, ChartTooltip, Legend);

function StateDetails() {
  const { stateId } = useParams();
  const [counties, setCounties] = useState([]);
  const [carriers, setCarriers] = useState([]);
  const [stateDetails, setStateDetails] = useState({});
  const [isEditing, setIsEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [internalUsers, setInternalUsers] = useState([]);
  const [internalUsersLoading, setInternalUsersLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [openDialog, setOpenDialog] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [selectedCounty, setSelectedCounty] = useState(null);
  const [countyName, setCountyName] = useState("");
  const [countyCarrier, setCountyCarrier] = useState(null);
  const [homesteadPercentage, setHomesteadPercentage] = useState("");
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [stateName, setStateName] = useState('');
  const [tabValue, setTabValue] = useState(0);
  const [bondGraphData, setBondGraphData] = useState({ labels: [], datasets: [{ label: 'Bond History', backgroundColor: '#1976d2', data: [] }] });

  // Company addresses
  const [addresses, setAddresses] = useState([]);
  const [pageAddr, setPageAddr] = useState(0);
  const [rowsPerPageAddr, setRowsPerPageAddr] = useState(10);
  const [openAddressDialog, setOpenAddressDialog] = useState(false);
  const [editingAddress, setEditingAddress] = useState(null);
  const [addressForm, setAddressForm] = useState({
    addr_type: '',
    name: '',
    street_address_1: '',
    street_address_2: '',
    city: '',
    county_name: '',
    primary_contact_name: '',
    primary_contact_cell: '',
    primary_contact_email: '',
    secondary_contact_name: '',
    secondary_contact_cell: '',
    secondary_contact_email: ''
  });
  const [addressCounty, setAddressCounty] = useState(null);
  const [cities, setCities] = useState([]);
  const [citiesLoading] = useState(false);

  // Holidays
  const [holidays, setHolidays] = useState([]);
  const [pageHol, setPageHol] = useState(0);
  const [rowsPerPageHol, setRowsPerPageHol] = useState(10);
  const [openHolidayDialog, setOpenHolidayDialog] = useState(false);
  const [editingHoliday, setEditingHoliday] = useState(null);
  const [holidayForm, setHolidayForm] = useState({
    holiday_name: '',
    holiday_type: '',
    holiday_start_date: '',
    holiday_end_date: '',
    is_active: true
  });

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      const stateNameFromStorage = localStorage.getItem("state_name");
      setStateName(stateNameFromStorage);

      try {
        setInternalUsersLoading(true);
        const results = await Promise.allSettled([
          apiClient(`/counties-by-state/${stateId}/`, "GET"),
          apiClient(`/carriers/0/`, "GET"),
          apiClient(`/states/${stateId}/`, "GET"),
          apiClient(`/internal-user`, "GET"),
          apiClient(`/company-addresses/state/${stateId}/`, "GET"),
          apiClient(`/holidays/state/${stateId}/`, "GET"),
          apiClient(`/fields/city/`, "GET"),
          apiClient(`/bonds-by-state/${stateId}/count-by-year/`, 'GET'),
        ]);

        const [countyRes, carrierRes, stateRes, usersRes, addrRes, holRes, citiesRes, bondsYearRes] = results;

        if (countyRes.status === 'fulfilled' && Array.isArray(countyRes.value)) {
          setCounties(countyRes.value);
        } else {
          console.warn('Counties fetch failed:', countyRes.reason);
          setCounties([]);
        }

        if (carrierRes.status === 'fulfilled' && carrierRes.value?.data && Array.isArray(carrierRes.value.data)) {
          setCarriers(carrierRes.value.data);
        } else {
          console.warn('Carriers fetch failed:', carrierRes.reason);
          setCarriers([]);
        }

        if (stateRes.status === 'fulfilled') {
          const sd = Array.isArray(stateRes.value) ? stateRes.value[0] : (stateRes.value?.data ?? stateRes.value);
          setStateDetails(sd || {});
        } else {
          console.warn('State details fetch failed:', stateRes.reason);
          setStateDetails({});
        }

        if (usersRes.status === 'fulfilled') {
          const val = usersRes.value;
          if (val?.data && Array.isArray(val.data)) setInternalUsers(val.data);
          else if (Array.isArray(val)) setInternalUsers(val);
          else setInternalUsers([]);
        } else {
          console.warn('Internal users fetch failed:', usersRes.reason);
          setInternalUsers([]);
        }

        if (addrRes.status === 'fulfilled') {
          const val = addrRes.value;
          setAddresses((val?.data && Array.isArray(val.data)) ? val.data : (Array.isArray(val) ? val : []));
        } else {
          console.warn('Company addresses fetch failed (treating as empty):', addrRes.reason);
          setAddresses([]);
        }

        if (holRes.status === 'fulfilled') {
          const val = holRes.value;
          setHolidays((val?.data && Array.isArray(val.data)) ? val.data : (Array.isArray(val) ? val : []));
        } else {
          console.warn('Holidays fetch failed (treating as empty):', holRes.reason);
          setHolidays([]);
        }

        if (citiesRes.status === 'fulfilled') {
          const val = citiesRes.value;
          const list = (val?.data && Array.isArray(val.data)) ? val.data : (Array.isArray(val) ? val : []);
          // Normalize to array of strings
          setCities(list.map((c) => (typeof c === 'string' ? c : (c?.city || c?.label || ''))).filter(Boolean));
        } else {
          console.warn('Cities fetch failed (defaulting to empty):', citiesRes.reason);
          setCities([]);
        }

        if (bondsYearRes.status === 'fulfilled') {
          const rows = Array.isArray(bondsYearRes.value) ? bondsYearRes.value : (bondsYearRes.value?.data || []);
          const labels = rows.map((r) => r.year);
          const data = rows.map((r) => r.count);
          setBondGraphData({
            labels,
            datasets: [
              {
                label: 'Bond History',
                backgroundColor: '#1976d2',
                data
              }
            ]
          });
        } else {
          console.warn('Bond count by year fetch failed:', bondsYearRes.reason);
          setBondGraphData({ labels: [], datasets: [{ label: 'Bond History', backgroundColor: '#1976d2', data: [] }] });
        }
      } catch (error) {
        console.error("Error fetching data:", error);
      } finally {
        setLoading(false);
        setInternalUsersLoading(false);
      }
    };

    fetchData();
  }, [stateId]);

  const handleSaveStateDetails = async () => {
    try {
      setSaving(true);
      const payload = {
        state_name: stateDetails.state_name,
        state_code: stateDetails.state_code,
        managed_by: stateDetails.managed_by,
        default_carrier_id: stateDetails.default_carrier_id,
        is_county_level: Boolean(stateDetails.is_county_level),
      };
      await apiClient(`/states/${stateId}/`, "PATCH", payload);
      setIsEditing(false);
      toast.success("State updated successfully");
      const refreshed = await apiClient(`/states/${stateId}/`, "GET");
      const sd = Array.isArray(refreshed) ? refreshed[0] : (refreshed?.data ?? refreshed);
      setStateDetails(sd || {});
    } catch (err) {
      console.error(err);
      toast.error("Failed to update state");
    } finally {
      setSaving(false);
    }
  };

  const handleOpenDialog = (county = null) => {
    if (county) {
      setEditMode(true);
      setSelectedCounty(county);
      setCountyName(county.bond_county);
      setHomesteadPercentage(county.homestead_percentage ?? "");

      const selectedCarrier = carriers.find((carrier) => carrier.bond_carrier_id === county.default_carrier_id);
      setCountyCarrier(selectedCarrier || null);
    } else {
      setEditMode(false);
      setCountyName("");
      setCountyCarrier(null);
      setHomesteadPercentage("");
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedCounty(null);
  };

  const handleSaveCounty = async () => {
    setSubmitting(true);
    try {
      const data = {
        bond_county: countyName,
        bond_state_id: stateId,
        default_carrier_id: countyCarrier?.bond_carrier_id || null,
        homestead_percentage: homesteadPercentage !== "" ? parseInt(homesteadPercentage) : null,
      };

      if (editMode) {
        await apiClient(`/counties/${selectedCounty.bond_county_id}/`, "PATCH", data);
        toast.success("County updated successfully.");
      } else {
        await apiClient(`/counties/create/`, "POST", data);
        toast.success("County added successfully.");
      }

      handleCloseDialog();

      const response = await apiClient(`/counties-by-state/${stateId}/`, "GET");
      if (Array.isArray(response)) setCounties(response);
    } catch (error) {
      console.error("Error saving county:", error);
      toast.error("Failed to save county.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const currentRows = counties.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);
  const currentAddrRows = addresses.slice(pageAddr * rowsPerPageAddr, pageAddr * rowsPerPageAddr + rowsPerPageAddr);
  const currentHolRows = holidays.slice(pageHol * rowsPerPageHol, pageHol * rowsPerPageHol + rowsPerPageHol);

  // Common table styling to match rounded cards UI
  const tableCardSx = {
    width: '100%',
    mb: 2,
    borderRadius: '12px',
    overflow: 'hidden',
    boxShadow: '0 2px 8px rgba(0,0,0,0.08)'
  };
  const headerRowSx = { backgroundColor: '#f5f5f5', '& th': { fontWeight: 600 } };
  const paginationSx = {
    backgroundColor: '#f8f9fa',
    borderTop: '1px solid #e0e0e0',
    '& .MuiTablePagination-toolbar': { color: '#1976d2' },
    '& .MuiTablePagination-selectIcon': { color: '#1976d2' }
  };

  const handleOpenAddressDialog = (addr = null) => {
    setEditingAddress(addr);
    setAddressForm(addr ? {
      addr_type: addr.addr_type || '',
      name: addr.name || '',
      street_address_1: addr.street_address_1 || '',
      street_address_2: addr.street_address_2 || '',
      city: addr.city || '',
      county_name: addr.county_name || '',
      primary_contact_name: addr.primary_contact_name || '',
      primary_contact_cell: addr.primary_contact_cell || '',
      primary_contact_email: addr.primary_contact_email || '',
      secondary_contact_name: addr.secondary_contact_name || '',
      secondary_contact_cell: addr.secondary_contact_cell || '',
      secondary_contact_email: addr.secondary_contact_email || ''
    } : addressForm);
    // try to map existing address county by id or name
    const byId = counties.find(
      (c) => c.bond_county_id === (addr?.county_id || addr?.county?.bond_county_id)
    );
    const byName = counties.find(
      (c) => (c.bond_county || '').toLowerCase() === (addr?.county_name || '').toLowerCase()
    );
    setAddressCounty(byId || byName || null);
    setOpenAddressDialog(true);
  };

  const handleSaveAddress = async () => {
    setSubmitting(true);
    try {
      const payload = { 
        ...addressForm, 
        state_id: stateId,
        county_id: addressCounty?.bond_county_id || null,
        county_name: addressCounty?.bond_county || addressForm.county_name || ''
      };
      if (editingAddress) {
        await apiClient(`/company-addresses/${editingAddress.company_address_id}/`, 'PATCH', payload);
      } else {
        await apiClient(`/company-addresses/`, 'POST', payload);
      }
      try {
        const res = await apiClient(`/company-addresses/state/${stateId}/`, 'GET');
        setAddresses((res?.data && Array.isArray(res.data)) ? res.data : (Array.isArray(res) ? res : []));
      } catch (err) {
        console.warn('Reload addresses failed (treating as empty):', err);
        setAddresses([]);
      }
      setOpenAddressDialog(false);
    } catch (e) { console.error(e); } finally { setSubmitting(false); }
  };

  const handleDeleteAddress = async (addrId) => {
    setSubmitting(true);
    try {
      await apiClient(`/company-addresses/${addrId}/`, 'DELETE');
      setAddresses(addresses.filter(a => a.company_address_id !== addrId));
    } catch (e) { console.error(e); } finally { setSubmitting(false); }
  };

  const handleOpenHolidayDialog = (hol = null) => {
    setEditingHoliday(hol);
    setHolidayForm(hol ? {
      holiday_name: hol.holiday_name || '',
      holiday_type: hol.holiday_type || '',
      holiday_start_date: hol.holiday_start_date || '',
      holiday_end_date: hol.holiday_end_date || '',
      is_active: hol.is_active ?? true
    } : holidayForm);
    setOpenHolidayDialog(true);
  };

  const handleSaveHoliday = async () => {
    setSubmitting(true);
    try {
      const payload = { ...holidayForm, state_id: stateId };
      if (editingHoliday) {
        await apiClient(`/holidays/${editingHoliday.holiday_id}/`, 'PATCH', payload);
      } else {
        await apiClient(`/holidays/`, 'POST', payload);
      }
      try {
        const res = await apiClient(`/holidays/state/${stateId}/`, 'GET');
        setHolidays((res?.data && Array.isArray(res.data)) ? res.data : (Array.isArray(res) ? res : []));
      } catch (err) {
        console.warn('Reload holidays failed (treating as empty):', err);
        setHolidays([]);
      }
      setOpenHolidayDialog(false);
    } catch (e) { console.error(e); } finally { setSubmitting(false); }
  };

  const handleDeleteHoliday = async (holId) => {
    setSubmitting(true);
    try {
      await apiClient(`/holidays/${holId}/`, 'DELETE');
      setHolidays(holidays.filter(h => h.holiday_id !== holId));
    } catch (e) { console.error(e); } finally { setSubmitting(false); }
  };

  const handleToggleHolidayActive = async (holiday) => {
    const original = Boolean(holiday.is_active);
    const next = !original;
    // Optimistic UI update
    setHolidays((prev) => prev.map((h) => h.holiday_id === holiday.holiday_id ? { ...h, is_active: next } : h));
    try {
      await apiClient(`/holidays/${holiday.holiday_id}/`, 'PATCH', { is_active: next });
    } catch (e) {
      toast.error('Failed to update holiday status.');
      // Revert on failure
      setHolidays((prev) => prev.map((h) => h.holiday_id === holiday.holiday_id ? { ...h, is_active: original } : h));
    }
  };

  const formatDateUS = (value) => {
    if (!value) return '—';
    const isoLike = /^\d{4}-\d{2}-\d{2}/;
    if (isoLike.test(value)) {
      const [y, m, d] = value.substring(0, 10).split('-');
      return `${m}/${d}/${y}`;
    }
    const dt = new Date(value);
    if (isNaN(dt)) return value;
    const mm = String(dt.getMonth() + 1).padStart(2, '0');
    const dd = String(dt.getDate()).padStart(2, '0');
    const yyyy = dt.getFullYear();
    return `${mm}/${dd}/${yyyy}`;
  };

  return (
    <>
      <Navbar />
      <Container maxWidth="xl" sx={{ marginTop: 4, marginBottom: 4 }}>
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
        <Card elevation={3} sx={{ borderRadius: '16px', height: '100%' }}>
          <CardHeader
            title={`State Details - ${stateName || (stateDetails.state_name || 'State')}`}
            sx={{
              backgroundColor: '#1976d2',
              color: 'white',
              '& .MuiCardHeader-title': { fontSize: '1.5rem', fontWeight: 600 }
            }}
          />
          <CardContent sx={{ p: 3 }}>
            <Box component="form">
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
                <TextField
                  label="State Name"
                  fullWidth
                  value={stateDetails.state_name || ''}
                  onChange={(e) => setStateDetails({ ...stateDetails, state_name: e.target.value })}
                  disabled={!isEditing}
                />
                <TextField
                  label="State Code"
                  fullWidth
                  value={stateDetails.state_code || ''}
                  onChange={(e) => setStateDetails({ ...stateDetails, state_code: e.target.value })}
                  disabled={!isEditing}
                />

                {/* Managed By Autocomplete */}
                <Autocomplete
                  options={internalUsers}
                  getOptionLabel={(user) => user.user?.username || ''}
                  loading={internalUsersLoading}
                  value={
                    internalUsers.find((u) => u.internal_user_id === stateDetails.managed_by) ||
                    internalUsers.find((u) => u.user?.username === stateDetails.managed_by_name) ||
                    null
                  }
                  onChange={(e, val) => setStateDetails({ ...stateDetails, managed_by: val ? val.internal_user_id : '' })}
                  disabled={!isEditing}
                  isOptionEqualToValue={(opt, val) => opt.internal_user_id === val.internal_user_id}
                  renderInput={(params) => (
                    <TextField {...params} label="Managed By" fullWidth disabled={!isEditing} placeholder="Search internal user..." />
                  )}
                  noOptionsText={internalUsersLoading ? 'Loading...' : 'No users'}
                />

                {/* Default Carrier Autocomplete */}
                <Autocomplete
                  options={carriers}
                  getOptionLabel={(carrier) => carrier.name || ''}
                  value={
                    carriers.find((c) => c.bond_carrier_id === stateDetails.default_carrier_id) || null
                  }
                  onChange={(e, val) => setStateDetails({ ...stateDetails, default_carrier_id: val ? val.bond_carrier_id : '' })}
                  readOnly={!isEditing}
                  isOptionEqualToValue={(opt, val) => opt.bond_carrier_id === val.bond_carrier_id}
                  renderInput={(params) => (
                    <TextField {...params} label="Default Carrier" fullWidth disabled={!isEditing} />
                  )}
                />

                <TextField
                  select
                  label="Goes to County Level"
                  fullWidth
                  value={stateDetails.is_county_level ? 'Yes' : 'No'}
                  onChange={(e) => setStateDetails({ ...stateDetails, is_county_level: e.target.value === 'Yes' })}
                  disabled={!isEditing}
                >
                  <MenuItem value="Yes">Yes</MenuItem>
                  <MenuItem value="No">No</MenuItem>
                </TextField>
              </Box>
              <Divider sx={{ my: 3 }} />
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
                {isEditing ? (
                  <>
                    <Button variant="outlined" onClick={() => setIsEditing(false)} sx={{ minWidth: 100 }}>
                      Cancel
                    </Button>
                    <Button variant="contained" onClick={handleSaveStateDetails} disabled={saving} sx={{ minWidth: 100 }}>
                      {saving ? <CircularProgress size={24} color="inherit" /> : 'Save'}
                    </Button>
                  </>
                ) : (
                  <Button variant="contained" onClick={() => setIsEditing(true)} sx={{ minWidth: 100 }}>
                    Edit
                  </Button>
                )}
              </Box>
            </Box>
          </CardContent>
        </Card>
          </Grid>

        {/* Bond History Card */}
          <Grid item xs={12} md={6}>
        <Card elevation={3} sx={{ borderRadius: '16px', height: '100%' }}>
          <CardHeader
            title="Bond History"
            sx={{
              backgroundColor: '#1565c0',
              color: 'white',
              '& .MuiCardHeader-title': { fontSize: '1.5rem', fontWeight: 600 }
            }}
          />
          <CardContent sx={{ padding: 3, marginBottom: 2 }}>
            {bondGraphData.labels.length === 0 ? (
              <Typography variant="body2" color="text.secondary">No data</Typography>
            ) : (
              <Bar
                data={bondGraphData}
                options={{
                  responsive: true,
                  plugins: {
                    legend: { display: false },
                    title: { display: false }
                  },
                  scales: { y: { ticks: { beginAtZero: true, precision: 0 } } }
                }}
              />
            )}
          </CardContent>
        </Card>
          </Grid>

        {/* Tabs for Counties / Company Addresses / Holidays */}
          <Grid item xs={12}>
        <Card elevation={3} sx={{ borderRadius: '16px', padding: 2 }}>
          <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
            <Tabs value={tabValue} onChange={(e, v) => setTabValue(v)} variant="scrollable" scrollButtons="auto">
              <Tab label={`Counties (${counties.length})`} />
              <Tab label={`Company Addresses (${addresses.length})`} />
              <Tab label={`Holidays (${holidays.length})`} />
            </Tabs>
          </Box>
          <CardContent sx={{ p: 0 }}>
            {/* Counties Tab */}
            {tabValue === 0 && (
              <>
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', p: 2, pt: 2 }}>
                  <Button variant="contained" color="primary" onClick={() => handleOpenDialog()}>
            Add County
          </Button>
        </Box>
        {loading ? (
          <Box display="flex" justifyContent="center" alignItems="center" sx={{ height: "200px" }}>
            <CircularProgress />
          </Box>
        ) : counties.length === 0 ? (
                  <Typography variant="body1" sx={{ p: 3 }}>No counties found for this state.</Typography>
        ) : (
                  <>
                    <TableContainer component={Paper} sx={tableCardSx}>
            <Table>
              <TableHead>
                          <TableRow sx={headerRowSx}>
                  <TableCell>County Name</TableCell>
                  <TableCell>Default Carrier</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {currentRows.map((county) => {
                  const carrier = carriers.find((c) => c.bond_carrier_id === county.default_carrier_id);
                  return (
                    <TableRow key={county.bond_county_id}>
                      <TableCell>{county.bond_county}</TableCell>
                      <TableCell>{carrier ? carrier.name : "N/A"}</TableCell>
                      <TableCell>
                        <IconButton color="primary" onClick={() => handleOpenDialog(county)}>
                          <EditIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        <TablePagination
          ActionsComponent={TablePaginationActions}
          component="div"
          count={counties.length}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          rowsPerPageOptions={[5, 10, 25]}
                      sx={paginationSx}
                    />
                  </>
                )}
              </>
            )}
            {/* Company Addresses Tab */}
            {tabValue === 1 && (
              <>
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', p: 2 }}>
                  <Button variant="contained" onClick={() => handleOpenAddressDialog()}>Add Address</Button>
                </Box>
                <TableContainer component={Paper} sx={tableCardSx}>
                  <Table>
                    <TableHead>
                      <TableRow sx={headerRowSx}>
                        <TableCell>Name</TableCell>
                        <TableCell>Type</TableCell>
                        <TableCell>Address</TableCell>
                        <TableCell>City</TableCell>
                        <TableCell>County</TableCell>
                        <TableCell>Primary Contact</TableCell>
                        <TableCell align="center">Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {currentAddrRows.map((addr) => (
                        <TableRow key={addr.company_address_id}>
                          <TableCell>{addr.name || 'N/A'}</TableCell>
                          <TableCell>{addr.addr_type || 'N/A'}</TableCell>
                          <TableCell>{[addr.street_address_1, addr.street_address_2].filter(Boolean).join(', ')}</TableCell>
                          <TableCell>{addr.city || 'N/A'}</TableCell>
                          <TableCell>{addr.county_name || (addr.county && addr.county.bond_county) || (addr.county_id ? (counties.find((c)=>c.bond_county_id===addr.county_id)?.bond_county) : 'N/A') || 'N/A'}</TableCell>
                          <TableCell>{addr.primary_contact_name || 'N/A'}</TableCell>
                          <TableCell align="center">
                            <IconButton color="primary" onClick={() => handleOpenAddressDialog(addr)}><EditIcon /></IconButton>
                            <IconButton color="error" onClick={() => handleDeleteAddress(addr.company_address_id)}><DeleteIcon /></IconButton>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
                <TablePagination
          ActionsComponent={TablePaginationActions}
                  component="div"
                  count={addresses.length}
                  page={pageAddr}
                  onPageChange={(e, p) => setPageAddr(p)}
                  rowsPerPage={rowsPerPageAddr}
                  onRowsPerPageChange={(e) => { setRowsPerPageAddr(parseInt(e.target.value, 10)); setPageAddr(0); }}
                  rowsPerPageOptions={[5, 10, 25]}
                  sx={paginationSx}
                />
              </>
            )}
            {/* Holidays Tab */}
            {tabValue === 2 && (
              <>
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', p: 2 }}>
                  <Button variant="contained" onClick={() => handleOpenHolidayDialog()}>Add Holiday</Button>
      </Box>
                <TableContainer component={Paper} sx={tableCardSx}>
                  <Table>
                    <TableHead>
                      <TableRow sx={headerRowSx}>
                        <TableCell>Name</TableCell>
                        <TableCell>Type</TableCell>
                        <TableCell>Start</TableCell>
                        <TableCell>End</TableCell>
                        <TableCell>Active</TableCell>
                        <TableCell align="center">Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {currentHolRows.map((h) => (
                        <TableRow key={h.holiday_id}>
                          <TableCell>{h.holiday_name}</TableCell>
                          <TableCell>{h.holiday_type || 'N/A'}</TableCell>
                          <TableCell>{formatDateUS(h.holiday_start_date)}</TableCell>
                          <TableCell>{formatDateUS(h.holiday_end_date)}</TableCell>
                          <TableCell>
                            <Switch
                              checked={Boolean(h.is_active)}
                              onChange={() => handleToggleHolidayActive(h)}
                              color="primary"
                            />
                          </TableCell>
                          <TableCell align="center">
                            <IconButton color="primary" onClick={() => handleOpenHolidayDialog(h)}><EditIcon /></IconButton>
                            <IconButton color="error" onClick={() => handleDeleteHoliday(h.holiday_id)}><DeleteIcon /></IconButton>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
                <TablePagination
          ActionsComponent={TablePaginationActions}
                  component="div"
                  count={holidays.length}
                  page={pageHol}
                  onPageChange={(e, p) => setPageHol(p)}
                  rowsPerPage={rowsPerPageHol}
                  onRowsPerPageChange={(e) => { setRowsPerPageHol(parseInt(e.target.value, 10)); setPageHol(0); }}
                  rowsPerPageOptions={[5, 10, 25]}
                  sx={paginationSx}
                />
              </>
            )}
          </CardContent>
        </Card>
          </Grid>
        </Grid>
      </Container>

      <Dialog open={openDialog} onClose={handleCloseDialog}>
        <DialogTitle>{editMode ? "Edit County" : "Add New County"}</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="County Name"
            type="text"
            fullWidth
            variant="outlined"
            value={countyName}
            onChange={(e) => setCountyName(e.target.value)}
          />
          <Autocomplete
            options={carriers}
            getOptionLabel={(carrier) => carrier.name || ""}
            value={countyCarrier || null}
            onChange={(e, selectedCarrier) => setCountyCarrier(selectedCarrier || null)}
            isOptionEqualToValue={(option, value) => option.bond_carrier_id === value.bond_carrier_id}
            renderInput={(params) => (
              <TextField {...params} label="Default Carrier" margin="dense" variant="outlined" fullWidth />
            )}
          />
          <TextField
            margin="dense"
            label="Homestead % to be Included"
            type="number"
            fullWidth
            variant="outlined"
            value={homesteadPercentage}
            onChange={(e) => setHomesteadPercentage(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} color="secondary">
            Cancel
          </Button>
          <Button onClick={handleSaveCounty} color="primary" disabled={submitting}>
            {submitting ? <CircularProgress size={20} /> : editMode ? "Save" : "Add"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Address Dialog */}
      <Dialog open={openAddressDialog} onClose={() => setOpenAddressDialog(false)}>
        <DialogTitle>{editingAddress ? 'Edit Address' : 'Add Address'}</DialogTitle>
        <DialogContent sx={{ width: '520px' }}>
          <TextField
            select
            margin="dense"
            label="Type"
            fullWidth
            value={addressForm.addr_type}
            onChange={(e) => setAddressForm({ ...addressForm, addr_type: e.target.value })}
          >
            <MenuItem value="Primary">Primary</MenuItem>
            <MenuItem value="Secondary">Secondary</MenuItem>
          </TextField>
          <TextField margin="dense" label="Name" fullWidth value={addressForm.name} onChange={(e) => setAddressForm({ ...addressForm, name: e.target.value })} />
          <TextField margin="dense" label="Street Address 1" fullWidth value={addressForm.street_address_1} onChange={(e) => setAddressForm({ ...addressForm, street_address_1: e.target.value })} />
          <TextField margin="dense" label="Street Address 2" fullWidth value={addressForm.street_address_2} onChange={(e) => setAddressForm({ ...addressForm, street_address_2: e.target.value })} />
          <Autocomplete
            options={cities.map((city) => ({ label: city, value: city }))}
            value={addressForm.city ? { label: addressForm.city, value: addressForm.city } : null}
            onChange={(e, val) => setAddressForm({ ...addressForm, city: val ? val.value : '' })}
            loading={citiesLoading}
            isOptionEqualToValue={(opt, val) => opt.value === val.value}
            renderInput={(params) => (
              <TextField {...params} margin="dense" label="City" variant="outlined" fullWidth />
            )}
          />
          <Autocomplete
            options={counties}
            getOptionLabel={(c) => c.bond_county || ''}
            value={addressCounty}
            onChange={(e, val) => setAddressCounty(val || null)}
            isOptionEqualToValue={(opt, val) => opt.bond_county_id === val.bond_county_id}
            renderInput={(params) => (
              <TextField {...params} margin="dense" label="County" variant="outlined" fullWidth />
            )}
          />
          <TextField margin="dense" label="Primary Contact Name" fullWidth value={addressForm.primary_contact_name} onChange={(e) => setAddressForm({ ...addressForm, primary_contact_name: e.target.value })} />
          <InputMask
            mask="999-999-9999"
            value={addressForm.primary_contact_cell || ''}
            onChange={(e) => setAddressForm({ ...addressForm, primary_contact_cell: e.target.value })}
          >
            {(inputProps) => (
              <TextField {...inputProps} margin="dense" label="Primary Contact Cell" fullWidth />
            )}
          </InputMask>
          <TextField margin="dense" label="Primary Contact Email" fullWidth value={addressForm.primary_contact_email} onChange={(e) => setAddressForm({ ...addressForm, primary_contact_email: e.target.value })} />
          <TextField margin="dense" label="Secondary Contact Name" fullWidth value={addressForm.secondary_contact_name} onChange={(e) => setAddressForm({ ...addressForm, secondary_contact_name: e.target.value })} />
          <InputMask
            mask="999-999-9999"
            value={addressForm.secondary_contact_cell || ''}
            onChange={(e) => setAddressForm({ ...addressForm, secondary_contact_cell: e.target.value })}
          >
            {(inputProps) => (
              <TextField {...inputProps} margin="dense" label="Secondary Contact Cell" fullWidth />
            )}
          </InputMask>
          <TextField margin="dense" label="Secondary Contact Email" fullWidth value={addressForm.secondary_contact_email} onChange={(e) => setAddressForm({ ...addressForm, secondary_contact_email: e.target.value })} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenAddressDialog(false)}>Cancel</Button>
          <Button onClick={handleSaveAddress} variant="contained" disabled={submitting}>{submitting ? <CircularProgress size={20} /> : 'Save'}</Button>
        </DialogActions>
      </Dialog>

      {/* Holiday Dialog */}
      <Dialog open={openHolidayDialog} onClose={() => setOpenHolidayDialog(false)}>
        <DialogTitle>{editingHoliday ? 'Edit Holiday' : 'Add Holiday'}</DialogTitle>
        <DialogContent sx={{ width: '520px' }}>
          <TextField margin="dense" label="Holiday Name" fullWidth value={holidayForm.holiday_name} onChange={(e) => setHolidayForm({ ...holidayForm, holiday_name: e.target.value })} />
          <TextField
            select
            margin="dense"
            label="Holiday Type"
            fullWidth
            value={holidayForm.holiday_type || ''}
            onChange={(e) => setHolidayForm({ ...holidayForm, holiday_type: e.target.value })}
          >
            <MenuItem value="Federal">Federal</MenuItem>
            <MenuItem value="State">State</MenuItem>
            <MenuItem value="Court Closure">Court Closure</MenuItem>
            <MenuItem value="Banking">Banking</MenuItem>
            <MenuItem value="Observed">Observed</MenuItem>
            <MenuItem value="Half Day">Half Day</MenuItem>
            <MenuItem value="Office Closed">Office Closed</MenuItem>
          </TextField>
          <TextField margin="dense" type="date" label="Start Date" fullWidth value={holidayForm.holiday_start_date} onChange={(e) => setHolidayForm({ ...holidayForm, holiday_start_date: e.target.value })} InputLabelProps={{ shrink: true }} />
          <TextField margin="dense" type="date" label="End Date" fullWidth value={holidayForm.holiday_end_date} onChange={(e) => setHolidayForm({ ...holidayForm, holiday_end_date: e.target.value })} InputLabelProps={{ shrink: true }} />
          <FormControlLabel
            control={
              <Checkbox
                checked={Boolean(holidayForm.is_active)}
                onChange={(e) => setHolidayForm({ ...holidayForm, is_active: e.target.checked })}
              />
            }
            label="Active"
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenHolidayDialog(false)}>Cancel</Button>
          <Button onClick={handleSaveHoliday} variant="contained" disabled={submitting}>{submitting ? <CircularProgress size={20} /> : 'Save'}</Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

export default StateDetails;
